import { readonly, ref, type DeepReadonly, type Ref } from 'vue';

export interface BulkActionLimits {
  /** Above this count, the selection indicator switches to a warning style and
   *  actions prompt a confirmation. */
  warnAt: number;
  /** Above this count, bulk actions are blocked (also enforced server-side). */
  blockAt: number;
}

/**
 * Runtime bulk-action thresholds served by the customer UI config
 * (`/rest/ui/config`) and set once during app bootstrap via
 * {@link setBulkActionLimits}. There is no fallback constant: the config
 * endpoint is a hard boot dependency, so a running app always has it, and the
 * field is product-scoped (only read where bulk actions are wired).
 */
const limits: Ref<BulkActionLimits | undefined> = ref(undefined);
const readonlyLimits = readonly(limits);

export function setBulkActionLimits(value: BulkActionLimits | undefined): void {
  limits.value = value;
}

/**
 * Read-only accessor: {@link setBulkActionLimits} (called once at boot) is the
 * single write path.
 */
export function useBulkActionLimits(): DeepReadonly<Ref<BulkActionLimits | undefined>> {
  return readonlyLimits;
}

/**
 * Where a selection count sits relative to the configured bulk-action limits:
 * - `disabled`: no limits configured → bulk actions are not available;
 * - `normal`: within `warnAt` → proceed silently;
 * - `warn`: `warnAt < count <= blockAt` → warning style + confirm before acting;
 * - `block`: `count > blockAt` → actions disabled (also enforced server-side).
 */
export type BulkActionState = 'disabled' | 'normal' | 'warn' | 'block';

/**
 * Pure evaluation of a selection count against the limits. `exempt` actions
 * (e.g. a firmware batch that sends one filter instead of enumerating stations)
 * bypass warn/block/confirm entirely.
 */
export function bulkActionStateFor(
  count: number,
  limits: BulkActionLimits | undefined,
  exempt = false
): BulkActionState {
  if (exempt) return 'normal';
  if (!limits) return 'disabled';
  if (count > limits.blockAt) return 'block';
  if (count > limits.warnAt) return 'warn';
  return 'normal';
}

/** A confirmation dialog is required only in the warn band (`warnAt < count <= blockAt`). */
export function bulkActionNeedsConfirm(state: BulkActionState): boolean {
  return state === 'warn';
}
